///---------------------------------------------------------------------------
//! Copyright (C) ASQUARED SRL - All Rights Reserved
//* Unauthorized copying of this file, via any medium is strictly prohibited
//* Proprietary and confidential
//* Written by Alexandru Gârbacea <g99.alex@yahoo.com>, September 2022
//? @author g99.alex@yahoo.com
///---------------------------------------------------------------------------

//-----------------------------------------------------------------------------------------
//* \brief
//? Registration, as three short steps instead of one wall of thirteen inputs.
//?
//? Two things do the actual work of making it shorter:
//?   - the CUI is looked up in ANAF/VIES and fills in the company name, trade register
//?     number, address, city and county, so five fields become one the user knows by heart;
//?   - every field is checked as it is left, under the field itself. The old form only
//?     validated on submit, printed the failures at the top and scrolled away from
//?     whatever the user was typing.
//?
//? The Firebase account is deliberately created only on the last step. Creating it at step
//? one would leave a trail of authenticated users with no company behind them, which the
//? rest of the app has no way to represent.
//-----------------------------------------------------------------------------------------

import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Link, useNavigate } from "react-router-dom";
import { validateVATEu, validateRegComAny, normalizeCui } from '../services/ValidationService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faSpinner,
  faCheck,
  faArrowRight,
  faArrowLeft,
  faMagnifyingGlass,
  faTriangleExclamation,
  faPenToSquare
} from '@fortawesome/free-solid-svg-icons'
import { useAuth } from '../context/AuthContext';
import { cities, counties } from '../databases/RoCities';
import { createNotification } from '../services/NotificationService';
import { lookupCompany } from '../services/CompanyLookupService';

const STEPS = ['Cont', 'Firmă', 'Contact']
const LAST_STEP = STEPS.length - 1

// eslint-disable-next-line no-useless-escape
const SPECIAL_CHARS = /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/
const PHONE_RE = /^[0-9]{10,12}$/

//? Which inputs belong to which step, so "is this step complete" is one lookup instead of
//? a condition repeated in the button, the Enter handler and the submit.
const STEP_FIELDS = [
  ['email', 'password', 'passwordCheck'],
  ['cui', 'compName', 'regCom', 'county', 'city', 'address'],
  ['name', 'surename', 'phone', 'agreePol', 'agreeTerm']
]

const EMPTY_FORM = {
  email: '',
  password: '',
  passwordCheck: '',
  cui: '',
  compName: '',
  regCom: '',
  address: '',
  city: '',
  county: '',
  name: '',
  surename: '',
  phone: '',
  tert: false,
  agreePol: false,
  agreeTerm: false
}

//? Module scope on purpose: declared inside Register it would be a new component type on
//? every keystroke, React would remount the input and the field would lose focus.
const Field = ({ id, label, hint, error, children }) => (
  <div className={`form-field${error ? ' form-field--invalid' : ''}`}>
    <label htmlFor={id}>{label}</label>
    {children}
    {hint && !error && <small className='field-hint'>{hint}</small>}
    {error && <small className='field-error'>{error}</small>}
  </div>
)

//-----------------------------------------------------------------------------------------
//* \brief
//? The password rules, as data rather than as a chain of early returns. The old version
//? reported them one per submit, so a password that broke two rules took two round trips
//? to fix; here all three are on screen from the first character typed.
//-----------------------------------------------------------------------------------------
const passwordRules = (pass) => ([
  { key: 'length', label: 'Cel puțin 8 caractere', ok: pass.length >= 8 },
  { key: 'digit', label: 'Cel puțin o cifră', ok: /\d/.test(pass) },
  { key: 'special', label: 'Cel puțin un caracter special', ok: SPECIAL_CHARS.test(pass) }
])

const Register = () => {
  const navigate = useNavigate();
  const { signup, currentUser, checkIfCompany } = useAuth()

  const [step, setStep] = useState(0)
  const [form, setForm] = useState(EMPTY_FORM)
  const [touched, setTouched] = useState({})
  //? Set when the user presses "Pas următor" on an incomplete step: the fields they never
  //? visited have to light up too, otherwise nothing explains why the button did nothing.
  const [forceShowErrors, setForceShowErrors] = useState(false)
  const [loading, setLoading] = useState(false)

  const [lookupStatus, setLookupStatus] = useState('idle') // idle | loading | found | notfound | error
  const [lookupSource, setLookupSource] = useState('')
  const [companyStatus, setCompanyStatus] = useState('')
  const [companyInactive, setCompanyInactive] = useState(false)
  const [editCompany, setEditCompany] = useState(false)
  //? A ref, not state: it is written and read inside the same tick by the debounce below,
  //? and a state update would not have landed yet when the next keystroke checks it.
  const lookedUpCui = useRef('')

  const [duplicate, setDuplicate] = useState(null)
  const [ip, setIP] = useState('');

  useEffect(() => {
    if (currentUser && Object.keys(currentUser).length > 0) {
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser])

  //-----------------------------------------------------------------------------------------
  //* \brief
  //? Best-effort IP for the signup record, plus a first guess at the city. Wrapped up
  //? because this is a third party service on the critical path of the page loading - it
  //? going down must not take the registration form with it.
  //-----------------------------------------------------------------------------------------
  useEffect(() => {
    const fetchGeo = async () => {
      try {
        const geoRes = await fetch('https://geolocation-db.com/json/').then(res => res.json())
        if (!geoRes) return
        if (geoRes.IPv4) setIP(geoRes.IPv4)
        if (geoRes.city) {
          const match = cities.find(city => city.toUpperCase() === geoRes.city.toUpperCase())
          //? Only ever a default: the CUI lookup overwrites it with the registered city.
          if (match) setForm(prev => (prev.city === '' ? { ...prev, city: match } : prev))
        }
      }
      catch (err) {
        // no IP and no prefilled city - neither is required to register
      }
    }
    fetchGeo()
  }, [])

  //-----------------------------------------------------------------------------------------
  //* \brief
  //? Resolves the CUI shortly after the user stops typing it, so the company details are
  //? already on screen by the time they look up from the keyboard.
  //?
  //? Deliberately not fired from onBlur: clicking "Pas următor" blurs the field first, so
  //? the lookup and the navigation would race and the first click would be swallowed.
  //-----------------------------------------------------------------------------------------
  useEffect(() => {
    if (step !== 1) return
    const cui = normalizeCui(form.cui)
    if (cui === '' || !validateVATEu(cui)) return
    if (lookedUpCui.current === cui) return

    const timer = setTimeout(() => runLookup(), 600)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.cui, step])

  const setField = (name, value) => {
    setForm(prev => ({ ...prev, [name]: value }))
    if (duplicate && name === 'cui') setDuplicate(null)
  }

  const markTouched = (name) => setTouched(prev => ({ ...prev, [name]: true }))

  //-----------------------------------------------------------------------------------------
  //* \brief
  //? Every field's error in one place, recomputed from the form. Nothing here writes state,
  //? so the button, the field markers and the submit all read the same answer.
  //-----------------------------------------------------------------------------------------
  const errors = useMemo(() => {
    const e = {}

    if (form.email.trim() === '') e.email = 'Introduceți adresa de email.'
    else if (!EMAIL_RE.test(form.email.trim())) e.email = 'Adresa de email nu este validă.'

    if (form.password === '') e.password = 'Introduceți o parolă.'
    else if (passwordRules(form.password).some(rule => !rule.ok)) {
      e.password = 'Parola nu îndeplinește toate condițiile.'
    }

    if (form.passwordCheck === '') e.passwordCheck = 'Repetați parola.'
    else if (form.password !== form.passwordCheck) e.passwordCheck = 'Parolele nu se potrivesc.'

    const cui = normalizeCui(form.cui)
    if (cui === '') e.cui = 'Introduceți CUI-ul firmei.'
    else if (cui.length < 4) e.cui = 'CUI-ul este prea scurt.'
    else if (!validateVATEu(cui)) e.cui = 'CUI invalid.'
    else if (duplicate) e.cui = 'Această firmă este deja înregistrată.'

    if (form.compName.trim() === '') e.compName = 'Introduceți numele firmei.'

    if (form.regCom.trim() === '') e.regCom = 'Introduceți numărul de la Registrul Comerțului.'
    else if (!validateRegComAny(form.regCom)) {
      e.regCom = 'Format acceptat: J40/1234/2020 sau J2024032303003.'
    }

    if (form.county.trim() === '') e.county = 'Alegeți județul.'
    if (form.city.trim() === '') e.city = 'Introduceți orașul.'
    if (form.address.trim() === '') e.address = 'Introduceți adresa firmei.'

    if (form.name.trim() === '') e.name = 'Introduceți numele.'
    if (form.surename.trim() === '') e.surename = 'Introduceți prenumele.'

    if (form.phone.trim() === '') e.phone = 'Introduceți numărul de telefon.'
    else if (!PHONE_RE.test(form.phone.trim())) {
      e.phone = 'Numărul trebuie să conțină între 10 și 12 cifre.'
    }

    if (!form.agreePol) e.agreePol = 'Este necesar acordul pentru a continua.'
    if (!form.agreeTerm) e.agreeTerm = 'Este necesar acordul pentru a continua.'

    return e
  }, [form, duplicate])

  const stepIsValid = (index) => STEP_FIELDS[index].every(name => !errors[name])
  const currentStepValid = stepIsValid(step)

  const errorFor = (name) => ((touched[name] || forceShowErrors) ? errors[name] : undefined)

  //-----------------------------------------------------------------------------------------
  //* \brief
  //? Resolves the CUI: fills the company fields from the official registers and, in the
  //? same pass, says whether the company already has an account here.
  //?
  //? Both checks happen on step two on purpose. The old form only discovered a company was
  //? already registered after all thirteen fields had been filled in and submitted.
  //-----------------------------------------------------------------------------------------
  const runLookup = async () => {
    const cui = normalizeCui(form.cui)

    if (cui === '' || !validateVATEu(cui)) {
      setLookupStatus('idle')
      return
    }

    //? Claimed before the first await: the debounce checks this to decide whether a CUI
    //? still needs resolving, and it must never start the same lookup twice.
    lookedUpCui.current = cui
    setLookupStatus('loading')
    setDuplicate(null)

    const [company, existing] = await Promise.all([
      lookupCompany(cui),
      checkIfCompany(cui)
    ])

    if (existing && existing.length > 0) {
      const taken = { cui, email: existing[0].email }
      setDuplicate(taken)
      setLookupStatus('idle')
      createNotification('warning',
        `Firma cu CUI ${cui} este deja înregistrată. Adresa de email asociată este ` +
        `${hideEmail(taken.email)}. Dacă totuși firma vă aparține vă rugăm să ne contactați.`,
        'Firma există deja', 12000)
      return
    }

    if (!company.found) {
      const reasons = { unavailable: 'error', withheld: 'withheld' }
      setLookupStatus(reasons[company.reason] || 'notfound')
      setEditCompany(true)
      return
    }

    setLookupSource(company.source)
    setCompanyStatus(company.status)
    setCompanyInactive(company.inactive)
    setForm(prev => ({
      ...prev,
      compName: company.name || prev.compName,
      regCom: company.regCom || prev.regCom,
      address: company.address || prev.address,
      city: company.city || prev.city,
      county: company.county || prev.county
    }))
    setLookupStatus('found')

    //? VIES answers with a name and one address line and nothing else, so a EU company
    //? still needs the rest by hand - open the fields rather than hide them behind a link.
    const incomplete = !company.regCom || !company.city || !company.county || !company.address
    setEditCompany(incomplete)
  }

  //? Explicit "Caută": clears the guard so the same CUI can be asked for again, which is
  //? the whole point of the button when the registry was down a moment ago.
  const retryLookup = () => {
    lookedUpCui.current = ''
    markTouched('cui')
    runLookup()
  }

  const hideEmail = (email) => {
    let hiddenEmail = "";
    for (let i = 0; i < email.length; i++) {
      if (i > 1 && i < email.indexOf("@")) hiddenEmail += "*";
      else hiddenEmail += email[i];
    }
    return hiddenEmail
  }

  const goToStep = (next) => {
    setStep(next)
    setForceShowErrors(false)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const goNext = () => {
    //? The company fields are still being written by the lookup - reporting them as
    //? missing right now would blame the user for the wait.
    if (step === 1 && lookupStatus === 'loading') return
    if (!currentStepValid) {
      setForceShowErrors(true)
      return
    }
    goToStep(step + 1)
  }

  const goBack = () => goToStep(Math.max(0, step - 1))

  //-----------------------------------------------------------------------------------------
  //* \brief
  //? Enter advances the wizard instead of submitting a half-filled registration.
  //-----------------------------------------------------------------------------------------
  const handleFormSubmit = async (e) => {
    e.preventDefault()
    if (step < LAST_STEP) return goNext()
    return submitRegistration()
  }

  const submitRegistration = async () => {
    if (!STEP_FIELDS.every((_, index) => stepIsValid(index))) {
      setForceShowErrors(true)
      //? Send them to the first step that still has something wrong with it.
      const broken = STEP_FIELDS.findIndex((_, index) => !stepIsValid(index))
      if (broken !== -1 && broken !== step) goToStep(broken)
      return
    }

    setLoading(true)
    const cui = normalizeCui(form.cui)

    //? Re-checked at the last moment: the lookup may have run minutes ago, and this is the
    //? one place where a duplicate would actually create a second account for a company.
    const existing = await checkIfCompany(cui)
    if (existing && existing.length > 0) {
      setDuplicate({ cui, email: existing[0].email })
      setLoading(false)
      goToStep(1)
      createNotification('warning',
        `Firma cu CUI ${cui} este deja înregistrată. Adresa de email asociată este ` +
        `${hideEmail(existing[0].email)}. Dacă totuși firma vă aparține vă rugăm să ne contactați.`,
        'Firma există deja', 12000)
      return
    }

    await signup(
      `${form.name.trim()} ${form.surename.trim()}`,
      form.email.trim(),
      form.phone.trim(),
      form.password,
      cui,
      form.compName.trim(),
      form.city.trim(),
      form.county.trim(),
      form.address.trim(),
      form.regCom.trim(),
      form.tert,
      false, // promo codes are not collected at registration
      ip.toString()
    )

    setLoading(false)
  }

  const percent = Math.round((step / STEPS.length) * 100)
  const rules = passwordRules(form.password)
  const companyResolved = lookupStatus === 'found' && !editCompany
  const nextBlocked = !currentStepValid || (step === 1 && lookupStatus === 'loading')

  return (
    <div className='login-wrapper no-bottom'>
      <div className='login-section'>
        <h1>Creare cont</h1>
        <p className='register-lead'>
          Trei pași scurți. Datele firmei se completează automat din CUI.
        </p>

        <div className='step-indicator'>
          Pasul <b>{step + 1}</b> / {STEPS.length}
          <span className='step-percent'>{STEPS[step]}</span>
        </div>
        <div className='width-wrapper'>
          <div className='progress-bar-wrapper'>
            <div className='progress-bar' style={{ width: `${percent}%` }}></div>
          </div>
        </div>

        <form className='login-form register-form' onSubmit={handleFormSubmit} noValidate>

          {/* ---------------------------------------------------------------- STEP 1 */}
          {step === 0 && (
            <div className='register-step'>
              <Field id='email' label='Email' error={errorFor('email')}
                hint='Aici primiți confirmarea contului și facturile.'>
                <input className='input-text' type='email' name='email' id='email'
                  value={form.email} autoComplete='email' autoFocus
                  onChange={e => setField('email', e.target.value)}
                  onBlur={() => markTouched('email')} />
              </Field>

              <Field id='password' label='Parolă' error={errorFor('password')}>
                <input className='input-text' type='password' name='password' id='password'
                  value={form.password} autoComplete='new-password'
                  onChange={e => setField('password', e.target.value)}
                  onBlur={() => markTouched('password')} />
              </Field>

              <ul className='password-rules'>
                {rules.map(rule => (
                  <li key={rule.key} className={rule.ok ? 'ok' : ''}>
                    <FontAwesomeIcon icon={faCheck} />
                    {rule.label}
                  </li>
                ))}
              </ul>

              <Field id='passwordCheck' label='Repetă parola' error={errorFor('passwordCheck')}>
                <input className='input-text' type='password' name='passwordCheck' id='passwordCheck'
                  value={form.passwordCheck} autoComplete='new-password'
                  onChange={e => setField('passwordCheck', e.target.value)}
                  onBlur={() => markTouched('passwordCheck')} />
              </Field>
            </div>
          )}

          {/* ---------------------------------------------------------------- STEP 2 */}
          {step === 1 && (
            <div className='register-step'>
              <Field id='cui' label='CUI firmă' error={errorFor('cui')}
                hint='Introduceți CUI-ul și restul datelor se completează automat.'>
                <div className='cui-lookup'>
                  <input className='input-text' type='text' name='cui' id='cui'
                    placeholder='RO1234567' value={form.cui} autoComplete='off' autoFocus
                    onChange={e => setField('cui', e.target.value)}
                    onBlur={() => { markTouched('cui'); setField('cui', normalizeCui(form.cui)) }} />
                  <button type='button' className='button-secondary btn-green cui-lookup__btn'
                    onClick={retryLookup} disabled={lookupStatus === 'loading'}>
                    <FontAwesomeIcon icon={lookupStatus === 'loading' ? faSpinner : faMagnifyingGlass}
                      className={lookupStatus === 'loading' ? 'spinner' : ''} />
                    Caută
                  </button>
                </div>
              </Field>

              {lookupStatus === 'notfound' && (
                <div className='lookup-note'>
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                  Nu am găsit firma în registrele oficiale. Completați datele manual mai jos.
                </div>
              )}
              {lookupStatus === 'error' && (
                <div className='lookup-note'>
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                  Registrul nu răspunde momentan. Puteți completa datele manual mai jos.
                </div>
              )}
              {lookupStatus === 'withheld' && (
                <div className='lookup-note'>
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                  Codul TVA este valid, dar statul emitent nu publică datele firmei.
                  Vă rugăm să le completați manual mai jos.
                </div>
              )}

              {companyResolved && (
                <div className='company-card'>
                  <div className='company-card__badge'>
                    <FontAwesomeIcon icon={faCheck} />
                    Firmă găsită{lookupSource === 'anaf' ? ' în registrul ANAF' : ' în registrul VIES'}
                  </div>
                  <div className='company-card__name'>{form.compName}</div>
                  <div className='company-card__meta'>{form.regCom} · {form.cui}</div>
                  <div className='company-card__meta'>
                    {form.address}, {form.city}, jud. {form.county}
                  </div>
                  {companyInactive && (
                    <div className='company-card__warn'>
                      <FontAwesomeIcon icon={faTriangleExclamation} />
                      Registrul indică starea: {companyStatus}
                    </div>
                  )}
                  <button type='button' className='link-button'
                    onClick={() => setEditCompany(true)}>
                    <FontAwesomeIcon icon={faPenToSquare} />
                    Datele nu sunt corecte? Modifică
                  </button>
                </div>
              )}

              {!companyResolved && (
                <>
                  <Field id='compName' label='Nume firmă' error={errorFor('compName')}>
                    <input className='input-text' type='text' name='compName' id='compName'
                      placeholder='Firma Mea SRL' value={form.compName} autoComplete='organization'
                      onChange={e => setField('compName', e.target.value)}
                      onBlur={() => markTouched('compName')} />
                  </Field>

                  <Field id='regCom' label='Nr. Registrul Comerțului' error={errorFor('regCom')}
                    hint='Ex.: J40/1234/2020 sau J2024032303003'>
                    <input className='input-text' type='text' name='regCom' id='regCom'
                      value={form.regCom} autoComplete='off'
                      onChange={e => setField('regCom', e.target.value)}
                      onBlur={() => markTouched('regCom')} />
                  </Field>

                  <Field id='county' label='Județ' error={errorFor('county')}>
                    <select className='input-text' name='county' id='county'
                      value={form.county}
                      onChange={e => setField('county', e.target.value)}
                      onBlur={() => markTouched('county')}>
                      <option value=''>Alegeți județul</option>
                      {[...counties].sort().map(county => (
                        <option key={county} value={county}>{county}</option>
                      ))}
                    </select>
                  </Field>

                  <Field id='city' label='Oraș' error={errorFor('city')}>
                    <input className='input-text' type='text' name='city' id='city'
                      list='cities-list' value={form.city} autoComplete='address-level2'
                      onChange={e => setField('city', e.target.value)}
                      onBlur={() => markTouched('city')} />
                    <datalist id='cities-list'>
                      {cities.map(city => <option key={city} value={city} />)}
                    </datalist>
                  </Field>

                  <Field id='address' label='Adresă' error={errorFor('address')}>
                    <input className='input-text' type='text' name='address' id='address'
                      value={form.address} autoComplete='street-address'
                      onChange={e => setField('address', e.target.value)}
                      onBlur={() => markTouched('address')} />
                  </Field>
                </>
              )}
            </div>
          )}

          {/* ---------------------------------------------------------------- STEP 3 */}
          {step === 2 && (
            <div className='register-step'>
              <Field id='name' label='Nume' error={errorFor('name')}>
                <input className='input-text' type='text' name='name' id='name'
                  value={form.name} autoComplete='family-name' autoFocus
                  onChange={e => setField('name', e.target.value)}
                  onBlur={() => markTouched('name')} />
              </Field>

              <Field id='surename' label='Prenume' error={errorFor('surename')}>
                <input className='input-text' type='text' name='surename' id='surename'
                  value={form.surename} autoComplete='given-name'
                  onChange={e => setField('surename', e.target.value)}
                  onBlur={() => markTouched('surename')} />
              </Field>

              <Field id='phone' label='Telefon' error={errorFor('phone')}>
                <input className='input-text' type='tel' name='phone' id='phone'
                  value={form.phone} autoComplete='tel'
                  onChange={e => setField('phone', e.target.value)}
                  onBlur={() => markTouched('phone')} />
              </Field>

              <div className='checkbox-wrapper'>
                <input className='checkbox' type='checkbox' id='tert' name='tert'
                  checked={form.tert}
                  onChange={e => setField('tert', e.target.checked)} />
                <label htmlFor='tert'>Declarant terț</label>
              </div>
              <small className='field-hint checkbox-hint'>
                Bifați dacă depuneți declarații Intrastat în numele altor firme.
              </small>

              <div className={`checkbox-wrapper${errorFor('agreePol') ? ' checkbox-wrapper--invalid' : ''}`}>
                <input className='checkbox' type='checkbox' id='agreePol' name='agreePol'
                  checked={form.agreePol}
                  onChange={e => { setField('agreePol', e.target.checked); markTouched('agreePol') }} />
                <label htmlFor='agreePol'>Sunt de acord cu&nbsp;
                  <Link to={'/politica-de-confidentialitate'} className='link'>Politica de confidențialitate.</Link>
                </label>
              </div>

              <div className={`checkbox-wrapper${errorFor('agreeTerm') ? ' checkbox-wrapper--invalid' : ''}`}>
                <input className='checkbox' type='checkbox' id='agreeTerm' name='agreeTerm'
                  checked={form.agreeTerm}
                  onChange={e => { setField('agreeTerm', e.target.checked); markTouched('agreeTerm') }} />
                <label htmlFor='agreeTerm'>Sunt de acord cu&nbsp;
                  <Link to={'/termeni-si-conditii'} className='link'>Termenii și condițiile.</Link>
                </label>
              </div>

              {(errorFor('agreePol') || errorFor('agreeTerm')) && (
                <small className='field-error'>
                  Acordul cu politica de confidențialitate și cu termenii este obligatoriu.
                </small>
              )}
            </div>
          )}

          <div className='steps-buttons'>
            {step > 0 && (
              <button type='button' className='button-secondary' onClick={goBack}>
                <FontAwesomeIcon icon={faArrowLeft} />
                Pas anterior
              </button>
            )}

            {step < LAST_STEP ? (
              <button type='submit'
                className={`button-secondary btn-green${nextBlocked ? ' disabled' : ''}`}>
                Pas următor
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            ) : loading ? (
              <FontAwesomeIcon className='spinner' icon={faSpinner} />
            ) : (
              <input className='input-submit' type='submit' value='Creare cont'
                disabled={!currentStepValid} />
            )}
          </div>

          <div className='register-footer'>Ai deja cont?
            <b><Link to={'/sign-in'} className='link'> Autentificare</Link></b>
          </div>
        </form>
      </div>
    </div>
  )
}

export default Register
